// Contenido debajo del hero V2 + navegación interna fija.
// Todas las secciones usan datos reales del producto; una sección sin
// contenido simplemente no se muestra.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ExternalLink, MapPin } from "lucide-react";

import { withoutRepeatedSummary } from "@/lib/presentation-text";
import { stripHtml } from "@/lib/slug";
import { cn } from "@/lib/utils";
import type { UnifiedTourDetail } from "@/lib/catalog.functions";

const NAV_OFFSET = 128;

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
}

export function TourV2Sections({
  tour: p,
  summary,
  desc,
}: {
  tour: UnifiedTourDetail;
  summary: string;
  desc: string;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState<string>("resumen");
  const [mapOpen, setMapOpen] = useState(false);

  const faqs = useMemo(() => {
    const items: { q: string; a: string }[] = [];
    const push = (q: string, a: string | null | undefined) => {
      const clean = stripHtml(a ?? "").trim();
      if (clean) items.push({ q, a: clean });
    };
    if (p.source !== "bokun") push(t("tour.cancellation") as string, p.cancellationPolicy);
    push(t("tour.requirements") as string, p.requirements);
    push(t("tour.beforeBooking") as string, p.importantInfo);
    push(
      t("tourV2.faqAccessibility", { defaultValue: "Accesibilidad" }) as string,
      p.accessibility,
    );
    push(t("tourV2.faqDressCode", { defaultValue: "Qué llevar" }) as string, p.dressCode);
    push(
      t("tourV2.faqCondition", { defaultValue: "Condición física" }) as string,
      p.physicalCondition,
    );
    return items;
  }, [p, t]);

  const sections = useMemo(() => {
    const list: { id: string; label: string }[] = [
      { id: "resumen", label: t("tourV2.nav.summary", { defaultValue: "Resumen" }) },
    ];
    if (p.itinerary.length > 0) list.push({ id: "itinerario", label: t("tour.itinerary") });
    if (p.included.length > 0 || p.excluded.length > 0)
      list.push({ id: "incluye", label: t("tour.includes") });
    if (p.startPoints.length > 0)
      list.push({ id: "punto-encuentro", label: t("tour.meetingPoint") });
    if (p.restrictions || p.dressCode || p.physicalCondition || p.importantInfo)
      list.push({
        id: "antes-de-viajar",
        label: t("tourV2.nav.before", { defaultValue: "Antes de viajar" }),
      });
    if (faqs.length > 0)
      list.push({
        id: "faq",
        label: t("tourV2.nav.faq", { defaultValue: "Preguntas frecuentes" }),
      });
    return list;
  }, [p, faqs.length, t]);

  useEffect(() => {
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter((e): e is HTMLElement => !!e);
    if (els.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: `-${NAV_OFFSET}px 0px -60% 0px`, threshold: 0 },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [sections]);

  const startPoint = p.startPoints[0];
  const mapQuery = startPoint
    ? startPoint.latitude && startPoint.longitude
      ? `${startPoint.latitude},${startPoint.longitude}`
      : [startPoint.address, startPoint.city].filter(Boolean).join(", ")
    : null;

  return (
    <>
      <nav
        aria-label={t("tourV2.nav.aria", { defaultValue: "Secciones del tour" }) as string}
        className="sticky top-[60px] z-30 -mx-4 mb-6 border-b border-border/70 bg-background/95 px-4 backdrop-blur lg:top-[68px] lg:mx-0 lg:rounded-none lg:px-0"
      >
        <ul className="flex gap-1 overflow-x-auto py-2 text-sm [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {sections.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => scrollToId(s.id)}
                aria-current={active === s.id ? "true" : undefined}
                className={cn(
                  "whitespace-nowrap rounded-full px-3.5 py-1.5 font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  active === s.id
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="space-y-5">
        <section id="resumen" className="scroll-mt-32">
          <h2 className="tour-display text-2xl">
            {t("tourV2.nav.summary", { defaultValue: "Resumen" })}
          </h2>
          {withoutRepeatedSummary(desc, summary) && <ExpandableText text={withoutRepeatedSummary(desc, summary)} />}
          {p.highlights.length > 0 && (
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {p.highlights.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-[0.95rem] leading-[1.6] text-foreground/85"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  {stripHtml(h)}
                </li>
              ))}
            </ul>
          )}
        </section>

        {p.itinerary.length > 0 && (
          <section
            id="itinerario"
            className="scroll-mt-32 rounded-3xl border border-border/70 bg-card p-5 sm:p-7"
          >
            <h2 className="tour-display text-2xl">{t("tour.itinerary")}</h2>
            <ol className="mt-5 space-y-5">
              {p.itinerary.map((it, i) => (
                <li key={i} className="relative pl-11">
                  <span className="absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <h3 className="text-base font-semibold">{it.title}</h3>
                  {it.body && (
                    <p className="mt-1.5 text-[0.95rem] leading-[1.7] text-foreground/80">
                      {stripHtml(it.body)}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {(p.included.length > 0 || p.excluded.length > 0) && (
          <section id="incluye" className="scroll-mt-32 grid gap-5 sm:grid-cols-2">
            {p.included.length > 0 && (
              <div className="rounded-3xl border border-border/70 bg-card p-5 sm:p-6">
                <h3 className="tour-display text-lg">{t("tour.includes")}</h3>
                <ul className="mt-3 space-y-2.5 text-[0.95rem] leading-[1.6]">
                  {p.included.map((x, i) => (
                    <li key={i} className="flex gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {stripHtml(x)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.excluded.length > 0 && (
              <div className="rounded-3xl border border-border/70 bg-card p-5 sm:p-6">
                <h3 className="tour-display text-lg">{t("tour.excludes")}</h3>
                <ul className="mt-3 space-y-2.5 text-[0.95rem] leading-[1.6] text-muted-foreground">
                  {p.excluded.map((x, i) => (
                    <li key={i}>· {stripHtml(x)}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {p.startPoints.length > 0 && (
          <section
            id="punto-encuentro"
            className="scroll-mt-32 rounded-3xl border border-border/70 bg-card p-5 sm:p-7"
          >
            <h2 className="tour-display inline-flex items-center gap-2 text-2xl">
              <MapPin className="h-5 w-5 text-primary" /> {t("tour.meetingPoint")}
            </h2>
            <ul className="mt-4 space-y-4">
              {p.startPoints.map((sp) => (
                <li key={sp.id} className="text-[0.95rem]">
                  <div className="font-semibold">{sp.title}</div>
                  {(sp.address || sp.city) && (
                    <div className="mt-0.5 text-muted-foreground">
                      {[sp.address, sp.city].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {sp.latitude && sp.longitude && (
                    <a
                      href={`https://maps.google.com/?q=${sp.latitude},${sp.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      {t("common.seeOnMaps")} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>

            {mapQuery && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setMapOpen((v) => !v)}
                  aria-expanded={mapOpen}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold transition hover:bg-accent"
                >
                  {mapOpen
                    ? t("tourV2.hideMap", { defaultValue: "Ocultar mapa" })
                    : t("tourV2.showMap", { defaultValue: "Ver mapa" })}
                  <ChevronDown
                    className={cn("h-4 w-4 transition", mapOpen && "rotate-180")}
                    aria-hidden
                  />
                </button>
                {/* El mapa se carga solo al abrir esta sección. */}
                {mapOpen && (
                  <iframe
                    title={t("tour.meetingPoint") as string}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="mt-3 h-64 w-full rounded-2xl border border-border/70"
                  />
                )}
              </div>
            )}
          </section>
        )}

        {(p.restrictions || p.dressCode || p.physicalCondition || p.importantInfo) && (
          <section
            id="antes-de-viajar"
            className="scroll-mt-32 rounded-3xl border border-border/70 bg-card p-5 sm:p-7"
          >
            <h2 className="tour-display text-2xl">
              {t("tourV2.nav.before", { defaultValue: "Antes de viajar" })}
            </h2>
            {[p.importantInfo, p.restrictions, p.dressCode, p.physicalCondition]
              .filter(Boolean)
              .map((txt, i) => (
                <p
                  key={i}
                  className="mt-3 whitespace-pre-line text-[0.95rem] leading-[1.7] text-foreground/85"
                >
                  {stripHtml(txt as string)}
                </p>
              ))}
          </section>
        )}

        {faqs.length > 0 && (
          <section
            id="faq"
            className="scroll-mt-32 rounded-3xl border border-border/70 bg-card p-5 sm:p-7"
          >
            <h2 className="tour-display text-2xl">
              {t("tourV2.nav.faq", { defaultValue: "Preguntas frecuentes" })}
            </h2>
            <div className="mt-3 divide-y divide-border/70">
              {faqs.map((f, i) => (
                <details key={i} className="group py-3">
                  <summary className="flex cursor-pointer items-center justify-between gap-3 text-[0.95rem] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                    {f.q}
                    <ChevronDown
                      className="h-4 w-4 shrink-0 transition group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <p className="mt-2 whitespace-pre-line text-[0.95rem] leading-[1.7] text-foreground/85">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function ExpandableText({ text }: { text: string }) {
  const { i18n } = useTranslation();
  const labels = { es: ["Leer más", "Mostrar menos"], en: ["Read more", "Show less"], fr: ["Lire la suite", "Afficher moins"], de: ["Mehr lesen", "Weniger anzeigen"], it: ["Leggi di più", "Mostra meno"], pt: ["Leia mais", "Mostrar menos"] };
  const lang = i18n.language.slice(0, 2) as keyof typeof labels;
  const copy = labels[lang] ?? labels.es;
  const [open, setOpen] = useState(false);
  const paragraphs = useMemo(() => text.split(/\n+/).filter(Boolean).slice(0, 20), [text]);
  const preview = paragraphs.slice(0, 2);
  const rest = paragraphs.slice(2);

  return (
    <div className="mt-4 space-y-3 text-[0.95rem] leading-[1.7] text-foreground/85">
      {preview.map((para, i) => (
        <p key={`p-${i}`}>{para}</p>
      ))}
      {rest.length > 0 && (
        <>
          <div
            className={cn(
              "grid transition-[grid-template-rows,opacity] duration-400 ease-out motion-reduce:transition-none",
              open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            )}
            aria-hidden={!open}
          >
            <div className="min-h-0 space-y-3 overflow-hidden">
              {rest.map((para, i) => (
                <p key={`r-${i}`}>{para}</p>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold transition hover:bg-accent"
          >
            {open
              ? copy[1]
              : copy[0]}
            <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} aria-hidden />
          </button>
        </>
      )}
    </div>
  );
}
